import React, { useState, useRef, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import Sidebar from "../components/Sidebar";
import "../styles/Expertpaper.css";
import { FaBell, FaUserCircle } from "react-icons/fa";
import axios from "axios";
import { toast } from "react-toastify";

const apiBase = "https://digital.tifleducation.com/admin/public/api";

const questionTypes = [
  { value: "M.C.Q", label: "M.C.Q" },
  { value: "Fill in the blanks", label: "Fill in the blanks" },
  { value: "True-False", label: "True-False" },
  { value: "Very Short Question Answer", label: "Very Short Question Answer" },
  { value: "Short Question Answer", label: "Short Question Answer" },
  { value: "Long Question Answer", label: "Long Question Answer" },
  { value: "Matching Types", label: "Matching Types" },
  { value: "Diagram-Based_Image-Based Questions", label: "Diagram-Based / Image-Based Questions" },
];

const Expertpaper = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // Board/Grade/Subject from location.state OR localStorage fallback
  let { boardId, gradeId, subjectId } = location.state || {};
  useEffect(() => {
    if (boardId && gradeId && subjectId) {
      localStorage.setItem("expertSelectedOptions", JSON.stringify({ boardId, gradeId, subjectId }));
    } else {
      const saved = localStorage.getItem("expertSelectedOptions");
      if (saved) {
        const { boardId: b, gradeId: g, subjectId: s } = JSON.parse(saved);
        if (!boardId) boardId = b;
        if (!gradeId) gradeId = g;
        if (!subjectId) subjectId = s;
      }
    }
  }, []);

  const [activeItem, setActiveItem] = useState("papers");
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [difficulties, setDifficulties] = useState([]);

  const [dashboardData, setDashboardData] = useState({
    totalBoard: 0,
    totalGrade: 0,
    totalSubjects: 0,
    totalPapers: 0,
    totalQuestions: 0,
  });

  const [user, setUser] = useState({ id: null, name: "", email: "", password: "" });
  const [chapters, setChapters] = useState([]);

  const [rows, setRows] = useState(() => {
    const savedRows = localStorage.getItem("expertRows");
    if (savedRows) {
      try {
        const parsed = JSON.parse(savedRows);
        return parsed.map(r => ({
          type: r.type || "",
          count: r.count || "",
          difficulty: r.difficulty || "",
          chapter: r.chapter?.toString() || ""
        }));
      } catch (e) {
        return [{ type: "", count: "", difficulty: "", chapter: "" }];
      }
    } else {
      return [{ type: "", count: "", difficulty: "", chapter: "" }];
    }
  });

  const [fetchedQuestions, setFetchedQuestions] = useState([]);
  const [loading, setLoading] = useState(false);

  // Save rows to localStorage
  useEffect(() => {
    const saveRows = rows.map(r => ({ ...r, chapter: r.chapter?.toString() || "" }));
    localStorage.setItem("expertRows", JSON.stringify(saveRows));
  }, [rows]);

  // Dashboard & user fetch
  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        const token = localStorage.getItem("token");
        const res = await axios.get(`${apiBase}/getDashboard`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.data) {
          setDashboardData({
            totalBoard: res.data.totalBoards || 0,
            totalGrade: res.data.totalGrades || 0,
            totalSubjects: res.data.totalSubjects || 0,
            totalPapers: res.data.totalPapers || 0,
            totalQuestions: res.data.totalQuestions || 0,
          });
          if (res.data.user) {
            setUser({
              id: res.data.user.id,
              name: res.data.user.name || "",
              email: res.data.user.email || "",
              password: "",
            });
          }
        }
      } catch (error) {
        console.error("Error fetching dashboard data:", error.response?.data || error);
      }
    };
    fetchDashboardData();
  }, []);

  // Fetch chapters
  useEffect(() => {
    const fetchChapters = async () => {
      if (!boardId || !gradeId || !subjectId) {
        setChapters([]);
        return;
      }
      try {
        const token = localStorage.getItem("token");
        setLoading(true);
        const res = await axios.post(
          `${apiBase}/papers/fetch-questions`,
          { board: boardId, grade: gradeId, subject: subjectId },
          { headers: { Authorization: `Bearer ${token}` } }
        );

        const data = res.data || {};
        let chaptersArray = [];

        if (Array.isArray(data)) {
          chaptersArray = data.map(ch => ({
            id: ch.id ?? ch.chapter_id ?? ch.chapterId,
            name: ch.chapter_name ?? ch.name ?? ch.title,
          }));
        } else if (data && typeof data === "object") {
          chaptersArray = Object.keys(data).map(key => {
            const ch = data[key] || {};
            return {
              id: Number(key),
              name: ch.chapter_name ?? ch.chapterName ?? ch.name ?? `Chapter ${key}`,
            };
          });
        }

        setChapters(chaptersArray);
        if (!chaptersArray.length) {
          toast.info("No chapters returned for selected board/grade/subject");
        }
      } catch (err) {
        console.error("Error fetching chapters:", err.response?.data || err);
        toast.error("Failed to load chapters for this subject");
      } finally {
        setLoading(false);
      }
    };
    fetchChapters();
  }, [boardId, gradeId, subjectId]);

  useEffect(() => {
    const fetchDifficulties = async () => {
      try {
        const token = localStorage.getItem("token");
        const res = await axios.get(`${apiBase}/difficulties`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (res.data && Array.isArray(res.data)) {
          setDifficulties(res.data);
        } else if (res.data && res.data.data && Array.isArray(res.data.data)) {
          setDifficulties(res.data.data);
        } else {
          console.warn("Unexpected difficulties API response", res.data);
          toast.info("No difficulties found from API");
        }
      } catch (error) {
        console.error("Error fetching difficulties:", error.response?.data || error);
        toast.error("Failed to load difficulty levels");
      }
    };

    fetchDifficulties();
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener("click", handleClickOutside, true);
    return () => document.removeEventListener("click", handleClickOutside, true);
  }, []);

  const toggleDropdown = () => setShowDropdown(prev => !prev);
  const addRow = () => setRows([...rows, { type: "", count: "", difficulty: "", chapter: "" }]);
  const removeRow = index => setRows(rows.filter((_, i) => i !== index));
  const handleChange = (index, field, value) => {
    const updated = [...rows];
    updated[index][field] = value.toString();
    setRows(updated);
  };

  // Fetch expert questions
  const handleFetchExpertQuestions = async () => {
    const savedOptions = JSON.parse(localStorage.getItem("expertSelectedOptions") || "{}");
    const boardToUse = boardId || savedOptions.boardId;
    const gradeToUse = gradeId || savedOptions.gradeId;
    const subjectToUse = subjectId || savedOptions.subjectId;

    if (!boardToUse || !gradeToUse || !subjectToUse) {
      toast.error("Board, Grade and Subject are required!");
      return;
    }

    const validRows = rows.filter(r => r.type && parseInt(r.count) > 0 && r.chapter !== "");
    if (!validRows.length) {
      toast.error("Please fill at least one question row with type, chapter and count");
      return;
    }

    try {
      const token = localStorage.getItem("token");
      setLoading(true);

      const payload = {
        board: boardToUse,
        grade: gradeToUse,
        subject: subjectToUse,
        expert_question_type: validRows.map(r => r.type),
        expert_no_of_questions: validRows.map(r => parseInt(r.count)),
        expert_difficulty: validRows.map(r => r.difficulty || null),
        expert_chapter: validRows.map(r => Number(r.chapter)),
      };

      const res = await axios.post(`${apiBase}/papers/fetch-random-questions`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const resp = res.data || {};
      let questionsArray = [];

      if (resp.status === "success" || resp.success === true) {
        if (Array.isArray(resp.questions)) {
          questionsArray = resp.questions;
        } else if (resp.questions && typeof resp.questions === "object") {
          questionsArray = Object.values(resp.questions).flatMap(q => Array.isArray(q) ? q : []);
        }

        if (!questionsArray.length) {
          toast.error("No questions returned by API for the selected filters");
          return;
        }

        let typedQuestions = [];
        validRows.forEach((row, rowIndex) => {
          const questionsOfType = questionsArray[rowIndex] || [];
          questionsOfType.forEach(q => {
            typedQuestions.push({ ...q, type: row.type });
          });
        });

        const previewPayload = {
          board: boardToUse,
          grade: gradeToUse,
          subject: subjectToUse,
          rows,
          questions: typedQuestions,
          fetchedAt: new Date().toISOString(),
        };
        localStorage.setItem("expertPreviewData", JSON.stringify(previewPayload));
        navigate("/expertpreview");
      } else {
        toast.error(resp.message || "Failed to fetch questions");
        console.error("fetch-random-questions error response", resp);
      }
    } catch (err) {
      console.error("Error fetching random questions:", err.response?.data || err);
      toast.error("Something went wrong while fetching questions");
    } finally {
      setLoading(false);
    }
  };

  const handleSaveProfile = async () => {
    try {
      const token = localStorage.getItem("token");
      await axios.post(
        `${apiBase}/update-profile`,
        { name: user.name, password: user.password },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Profile updated successfully!");
      setIsModalOpen(false);
      setUser({ ...user, password: '' });
    } catch (error) {
      console.error("Error updating profile:", error.response?.data || error);
      toast.error("Failed to update profile");
    }
  };

  return (
    <div className="dashboard-container">
      <Sidebar
        isSidebarOpen={isSidebarOpen}
        setIsSidebarOpen={setIsSidebarOpen}
        activeItem={activeItem}
        setActiveItem={setActiveItem}
      />
      <main className="main-content">
        <div className="expert-header">
          <div className="expert-welcome-text">
            <a href="/papers" className="back-link">
              <i className="fa fa-arrow-left" aria-hidden="true"></i>
            </a>
            <span>Expert Papers</span>
          </div>
          <div className="header-icons" ref={dropdownRef}>
            <FaBell className="icon" />
            <div className="user-dropdown-wrapper">
              <FaUserCircle className="icon" onClick={toggleDropdown} />
              {showDropdown && (
                <div className="user-dropdown">
                  <div className="edituser" onClick={() => setIsModalOpen(true)}>
                    <FaUserCircle className="ditusericon" />
                    <span>Edit Profile</span>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="title-section">
          <h3>Select Options<span style={{ color: "red" }}>*</span></h3>
        </div>

        <div className="fields-section">
          {rows.map((row, index) => (
            <div className="field-row" key={index}>
              <select value={row.type} onChange={(e) => handleChange(index, "type", e.target.value)}>
                <option value="">Select Question Type</option>
                {questionTypes.map(qt => (
                  <option key={qt.value} value={qt.value}>{qt.label}</option>
                ))}
              </select>

              <select value={row.chapter} onChange={(e) => handleChange(index, "chapter", e.target.value)}>
                <option value="">Select Chapter</option>
                {chapters.length ? (
                  chapters.map(ch => <option key={ch.id} value={ch.id.toString()}>{ch.name}</option>)
                ) : (
                  <option value="" disabled>Chapters not loaded — select board/grade/subject</option>
                )}
              </select>

              <input type="number" min={1} placeholder="No. of Questions" value={row.count} onChange={(e) => handleChange(index, "count", e.target.value)} />

              <select value={row.difficulty} onChange={(e) => handleChange(index, "difficulty", e.target.value)}>
                <option value="">Select Difficulty</option>
                {difficulties.length ? (
                  difficulties.map((d) => (
                    <option key={d.id} value={d.name || d.level || d.difficulty}>
                      {d.name || d.level || d.difficulty}
                    </option>
                  ))
                ) : (
                  <option value="" disabled>Loading difficulties...</option>
                )}
              </select>

              <button type="button" className="remove-btn" onClick={() => removeRow(index)}>-</button>
            </div>
          ))}

          <button type="button" className="add-btn" onClick={addRow}>+ Add</button>

          <div className="expert-next-button-container">
            <button type="button" className="expert-next-btn" onClick={handleFetchExpertQuestions} disabled={loading || chapters.length === 0}>
              {loading ? "Fetching..." : "Next"}
            </button>
          </div>
        </div>
      </main>

      {/* Profile Edit Modal */}
      {isModalOpen && (
        <div style={modalStyles.overlay}>
          <div style={modalStyles.modal}>
            <button style={modalStyles.closeBtn} onClick={() => setIsModalOpen(false)}>&times;</button>
            <div style={modalStyles.userIcon}><FaUserCircle /></div>

            <div style={modalStyles.formGroup}>
              <label>Username</label>
              <input type="text" value={user.name} onChange={(e) => setUser({ ...user, name: e.target.value })} style={modalStyles.input} />
            </div>

            <div style={modalStyles.formGroup}>
              <label>Email</label>
              <input type="email" value={user.email} disabled style={{ ...modalStyles.input, background: '#f0f0f0', color: '#888' }} />
            </div>

            <div style={modalStyles.formGroup}>
              <label>Password</label>
              <input type="password" value={user.password} onChange={(e) => setUser({ ...user, password: e.target.value })} style={modalStyles.input} />
            </div>

            <div style={{ textAlign: 'right' }}>
              <button style={modalStyles.saveBtn} onClick={handleSaveProfile}>Save Changes</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const modalStyles = {
  overlay: { position: "fixed", top: 0, left: 0, width: "100%", height: "100%", background: "rgba(0,0,0,0.6)", display: "flex", justifyContent: "center", alignItems: "center", zIndex: 1000 },
  modal: { background: "#fff", padding: "2rem", borderRadius: "12px", width: "90%", maxWidth: "400px", position: "relative", boxShadow: "0 4px 15px rgba(0,0,0,0.2)" },
  closeBtn: { position: "absolute", top: "10px", right: "15px", background: "none", border: "none", fontSize: "1.5rem", cursor: "pointer" },
  userIcon: { textAlign: "center", fontSize: "4rem", color: "#129255", marginBottom: "1rem" },
  formGroup: { marginBottom: "1rem" },
  input: { width: "100%", padding: "0.5rem 0.8rem", borderRadius: "6px", border: "1px solid #ccc", outline: "none" },
  saveBtn: { background: "#129255", color: "#fff", border: "none", padding: "0.6rem 1.2rem", borderRadius: "6px", cursor: "pointer", fontWeight: "bold" }
};

export default Expertpaper;
